import React, { useContext, useEffect, useState, useMemo } from 'react';
import addtoschedule from './Assets/Icons/AddSchedule.svg';
import removeFromSchedule from './Assets/Icons/RemoveFromSchedule.svg';
import ChevronUp from './Assets/Icons/ChevronUp.svg';
import ChevronDown from './Assets/Icons/ChevronDown.svg';
import UserContext from "../../contexts/user";
import userImg from './Assets/Icons/User.png'
import quiz from './Assets/Icons/Quiz.svg';
import forum from './Assets/Icons/Forum.svg';
import axios from 'axios';
import { timeFromMinutes } from "../../helpers";
import EventContext from "../../contexts/event";
import { useHistory } from 'react-router-dom';
import Cluster from './cluster';
import leftArrow from './Assets/Icons/LeftArrow.svg'
import { EslidesEvent, Presentation } from '../../types/presentation';
import views from './Assets/Icons/eye.svg'
import { useQueryClient } from 'react-query';
import { Calendar, Users2 } from 'lucide-react'

// Cache for speaker images
const speakerImageCache: { [key: string]: string } = {};


// Helper function to convert time in minutes to hours
const minutesToHours = (minutes: number): number => {
    return minutes / 60;
};

// Updated helper function to calculate position based on time - aligned with Timeline
const calculatePosition = (
    time: number,
    containerHeight: number,
    startHour: number = 1,
    endHour: number = 24
): { top: number; height: number } => {
    const timeInHours = minutesToHours(time);
    const totalHours = endHour - startHour + 1; // +1 because we include both start and end hours
    const hourHeight = containerHeight / totalHours;
    const timelineOffset = 0; // Matches timeline wrapper top padding (lg:tw-pt-5)

    // Calculate position relative to start hour
    const relativeHour = timeInHours - startHour;

    // Align cards/clusters to the same baseline used by hour marker guides in synopsis.tsx
    const calculatedTop = Math.max(0, (relativeHour * hourHeight) + (hourHeight / 2) + timelineOffset);
    const top = calculatedTop;

    return { top, height: hourHeight };
};

// Helper function to calculate card height based on duration
const calculateCardHeight = (
    startTime: number,
    endTime: number,
    containerHeight: number,
    startHour: number = 1,
    endHour: number = 24,
    minHeight: number = 120
): number => {
    const startTimeInHours = minutesToHours(startTime);
    const endTimeInHours = minutesToHours(endTime);
    const duration = endTimeInHours - startTimeInHours;

    const totalHours = endHour - startHour + 1;
    const hourHeight = containerHeight / totalHours;

    const calculatedHeight = duration * hourHeight;
    return Math.max(minHeight, calculatedHeight);
};

// Helper function to calculate height based on zoom level
const calculateZoomBasedHeight = (zoomLevel: number[], zoomIndex: number): number => {
    const baseHeight = 105;
    const maxHeight = 130;
    const minHeight = 45;

    // Calculate the height based on zoom level
    const zoomPercentage = zoomLevel[zoomIndex];

    // Handle different zoom levels
    switch (zoomPercentage) {
        case 1:
            return minHeight;
        case 25:
            return minHeight + 50;
        case 50:
            return baseHeight;
        case 75:
            return Math.round((minHeight + baseHeight));
        case 100:
            return Math.round((maxHeight + baseHeight));
        default:
            return baseHeight;
    }
};

// Returns true if this presentation is the only one in its time window and duration <= 1 hour
const isSingleShortPresentation = (pres: Presentation, allPresentations: Presentation[]) => {
    const duration = pres.end_time - pres.time;
    if (duration > 60) return false;
    // Count how many presentations overlap this time window
    const overlapping = allPresentations.filter(
        p => p._id !== pres._id &&
            ((p.time < pres.end_time && p.end_time > pres.time))
    );
    return overlapping.length === 0;
};

// Map the current zoomLevel to the effective zoomLevel for singleShort presentations
const getEffectiveZoomLevel = (zoomLevel: number[], zoomIndex: number): number => {
    const current = zoomLevel[zoomIndex];
    if (current === 50) return 25;
    if (current === 75) return 50;
    if (current === 1) return 50;
    return current;
};

interface PresentationCardsProps {
    presentation: any,
    updatePresentation?: (presentation: any) => void;
    day: string;
    rooms: { [roomId: string]: Presentation[] };
    containerHeight?: number;
    startHour?: number;
    endHour?: number;
    event?: EslidesEvent;
    zoomLevel: number[];
    zoomIndex: number;
    isMobileOrTablet?: boolean;
}

const PresentationCards: React.FC<PresentationCardsProps> = ({
    presentation,
    updatePresentation,
    day,
    rooms,
    containerHeight = 1080,
    startHour = 1,
    endHour = 24,
    event,
    zoomLevel,
    zoomIndex,
    isMobileOrTablet = false
}) => {

    // Use timeline height from parent to keep cards/clusters aligned with hour markers at every zoom level
    const dynamicContainerHeight = containerHeight;

    const userContext = useContext(UserContext);
    const eventContext = useContext(EventContext);
    const history = useHistory();
    const [speakerImages, setSpeakerImages] = useState<{ [key: string]: string }>({});
    const [speakerNames, setSpeakerNames] = useState<{ [key: string]: string }>({});
    const [expandedPresentations, setExpandedPresentations] = useState<{ [key: string]: boolean }>({});
    const [scheduledPresentations, setScheduledPresentations] = useState<{ [key: string]: boolean }>({});
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [plays, setPlays] = useState(0);
    const [isClusterOpen, setIsClusterOpen] = useState(false);
    const [selectedClusterPresentation, setSelectedClusterPresentation] = useState<any | null>(null);
    const [selectedClusterGroup, setSelectedClusterGroup] = useState<Presentation[] | null>(null);
    const [isClusterDrawerOpen, setIsClusterDrawerOpen] = useState(false);
    const [openedClusterPresentations, setOpenedClusterPresentations] = useState<Presentation[]>([]);
    const [isUpdating, setIsUpdating] = useState<{ [key: string]: boolean }>({});
    const queryClient = useQueryClient();
    // Get event_slug from URL for use in query invalidation/refetch
    const event_slug = document.location.pathname.split("/")[1] || "invalid";


    // Memoize speaker info to prevent unnecessary recalculations
    const speakerInfoMap = useMemo(() => {
        const info: { [key: string]: { name: string, picture: string } } = {};
        presentation?.forEach((pres: any) => {
            const speakerInfo = pres.speaker_info;
            const speakerId = pres.speaker_id;
            if (speakerInfo?._id === speakerId) {
                info[pres._id] = {
                    name: `${speakerInfo?.firstName} ${speakerInfo?.lastName}`,
                    picture: speakerInfo?.picture
                };
            }
        });
        return info;
    }, [presentation]);

    useEffect(() => {
        // Initialize schedule state for each presentation
        const initialScheduleState: { [key: string]: boolean } = {};
        presentation?.forEach((pres: any) => {
            initialScheduleState[pres._id] = pres.users?.some((user: any) => user.id === userContext?.user?.id) || false;
        });
        setScheduledPresentations(initialScheduleState);
    }, [presentation, userContext?.user?.id]);

    useEffect(() => {
        const fetchSpeakerPictures = async () => {
            if (!presentation?.length) return;
            try {
                setIsLoadingImages(true);
                const newSpeakerImages: { [key: string]: string } = {};
                const newSpeakerNames: { [key: string]: string } = {};

                // Set speaker names immediately
                Object.entries(speakerInfoMap).forEach(([presId, info]) => {
                    newSpeakerNames[presId] = info.name;
                });

                // Fetch images in parallel
                const imagePromises = Object.entries(speakerInfoMap).map(async ([presId, info]) => {
                    // Check cache first
                    if (speakerImageCache[info.picture]) {
                        newSpeakerImages[presId] = speakerImageCache[info.picture];
                        return;
                    }

                    try {
                        const response = await axios.post(
                            `${process.env.REACT_APP_API_ENDPOINT}/api/s3/images`,
                            { key: info.picture },
                            { headers: { 'Content-Type': 'application/json' } }
                        );

                        if (response.data.data) {
                            const imageUrl = response.data.data.signedUrl;
                            speakerImageCache[info.picture] = imageUrl;
                            newSpeakerImages[presId] = imageUrl;
                        }
                    } catch (error) {
                        console.error("Failed to fetch signed URL:", error);
                    }
                });

                await Promise.all(imagePromises);
                setSpeakerImages(newSpeakerImages);
                setSpeakerNames(newSpeakerNames);
                setIsLoadingImages(false);
            } catch (error) {
                console.error("Critical error in fetchSpeakerPictures:", error);
            }
        };

        fetchSpeakerPictures();
    }, [presentation, speakerInfoMap]);

    // Clear selectedClusterPresentation when drawer closes
    useEffect(() => {
        if (!isDrawerOpen) {
            setSelectedClusterPresentation(null);
            setSelectedClusterGroup(null);
        }
    }, [isDrawerOpen]);

    const toggleExpand = (presentationId: string) => {
        setExpandedPresentations(prev => ({
            ...prev,
            [presentationId]: !prev[presentationId]
        }));
    };
    //for updating the schedule status of a presentation
    const updateScheduleStatus = async (presentationId: string, eventId: string, isScheduled: boolean) => {
        if (!userContext?.user?.id) {
            console.warn("User not authenticated");
            return;
        }

        // setScheduledPresentations(prev => ({
        //     ...prev,
        //     [presentationId]: !isScheduled
        // }));

        try {
            setIsUpdating(prev => ({ ...prev, [presentationId]: true }));

            let response;
            if (!isScheduled) {
                // Add to schedule
                response = await axios.patch(
                    `${process.env.REACT_APP_API_ENDPOINT}/api/speaker-data/${presentationId}/my-schedule/${eventId}`,
                    { id: userContext.user.id },
                    { timeout: 10000 }
                );
            } else {
                // Remove from schedule
                response = await axios.delete(
                    `${process.env.REACT_APP_API_ENDPOINT}/api/speaker-data/${presentationId}/my-schedule/${eventId}/${userContext.user.id}`,
                    { timeout: 10000 }
                );
            }

            // Update parent component's state
            if (updatePresentation && response.data) {
                updatePresentation(response.data);
            }

            // Invalidate queries
            queryClient.invalidateQueries("event");
            queryClient.invalidateQueries(["event", event_slug]);

        } catch (error) {
            console.error(`Failed to ${isScheduled ? 'remove from' : 'add to'} schedule:`, error);

            setScheduledPresentations(prev => ({
                ...prev,
                [presentationId]: isScheduled
            }));

        } finally {
            setIsUpdating(prev => ({ ...prev, [presentationId]: false }));
        }
    };

    // Group presentations by (start, end) time - only exact matches, and only if group size > 1
    const presentationsByExactTime: { [key: string]: Presentation[] } = {};
    presentation.forEach((pres: Presentation) => {
        const key = `${pres.time}-${pres.end_time}`;
        if (!presentationsByExactTime[key]) presentationsByExactTime[key] = [];
        presentationsByExactTime[key].push(pres);
    });
    // Only keep groups with more than one presentation
    const exactTimeGroups: Presentation[][] = Object.values(presentationsByExactTime).filter(group => group.length > 1);
    const exactTimePresentationIds = new Set(exactTimeGroups.flatMap(group => group.map(p => p._id)));

    // Group presentations by hour (never cross an hour boundary), excluding those already in exactTimePresentationIds
    // Note: presentations ending exactly on the hour boundary (e.g. 1:45-2:00) still count as "within the hour".
    const groupPresentationsByHour = (presentations: Presentation[]): Presentation[][] => {
        const sortedPresentations = [...presentations].sort((a, b) => a.time - b.time);
        const grouped: Presentation[][] = [];
        let currentGroup: Presentation[] = [];
        let currentHour: number | null = null;

        const flush = () => {
            // changed from 4 to 2 to show groups of 2 or more Date: October 22 2025
            if (currentGroup.length >= 2) grouped.push(currentGroup);
            currentGroup = [];
            currentHour = null;
        };

        for (const pres of sortedPresentations) {
            if (exactTimePresentationIds.has(pres._id)) continue;

            const presHour = Math.floor(pres.time / 60);
            const hourEnd = (presHour + 1) * 60;
            const endsWithinSameHour = pres.end_time <= hourEnd;

            // If a presentation runs past the hour, don't cluster it (prevents hour-mixing edge cases)
            if (!endsWithinSameHour) {
                flush();
                continue;
            }

            if (currentGroup.length === 0) {
                currentGroup = [pres];
                currentHour = presHour;
                continue;
            }

            if (presHour === currentHour) {
                currentGroup.push(pres);
            } else {
                flush();
                currentGroup = [pres];
                currentHour = presHour;
            }
        }

        flush();
        return grouped;
    };

    const timeGroupedPresentationGroups = groupPresentationsByHour(presentation);
    const timeGroupedPresentationIds = new Set(timeGroupedPresentationGroups.flatMap(group => group.map(p => p._id)));

    // Filter out both presentations with same time and time grouped presentations
    const filteredPresentations: Presentation[] = presentation.filter(
        (pres: Presentation) =>
            !exactTimePresentationIds.has(pres._id) &&
            !timeGroupedPresentationIds.has(pres._id)
    );

    const clusterGroups: Presentation[][] = [...exactTimeGroups, ...timeGroupedPresentationGroups]
        .map(group => [...group].sort((a, b) => a.time - b.time))
        .filter(group => group.length > 0);

    const selectedClusterGroupKey = selectedClusterGroup
        ? selectedClusterGroup.map(p => p._id).sort().join("|")
        : null;

    //to show the total plays count if the user not logged in.
    let token: any = localStorage.getItem(`token-${event_slug}`);

    useEffect(() => {
        const fetchPlaysCount = async () => {
            if (!presentation._id) return;

            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_ENDPOINT}/api/presentation/${presentation._id}/plays`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        timeout: 8000
                    }
                );

                if (response.data?.data !== undefined) {
                    setPlays(response.data.data);
                } else {
                    console.warn("Invalid plays data structure received");
                    setPlays(0);
                }
            } catch (error) {
                console.error("Failed to fetch plays count:", error);
                setPlays(0);
            }
        };

        fetchPlaysCount();
    }, [presentation._id, token]);

    const handleDrawerOpen = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    return (
        <div className="tw-relative tw-w-full tw-h-full" style={{ height: `${dynamicContainerHeight}px` }}>
            {filteredPresentations && filteredPresentations.map((pres: any, index: number) => {
                const date = new Date(pres.date);
                const formattedDate = `${date.getDate()}/${date.toLocaleString('en-US', { month: 'short' })}`;

                // Calculate position based on presentation start time using dynamic container height
                const startPosition = calculatePosition(pres.time, dynamicContainerHeight, startHour, endHour);

                // Calculate if this is a singleShort presentation
                const singleShort = isSingleShortPresentation(pres, filteredPresentations);

                // Use custom zoom mapping for singleShort presentations
                const effectiveZoom = singleShort ? getEffectiveZoomLevel(zoomLevel, zoomIndex) : zoomLevel[zoomIndex];

                // Calculate card height based on presentation duration and effective zoom level
                const baseCardHeight = calculateCardHeight(
                    pres.time,
                    pres.end_time,
                    dynamicContainerHeight,
                    startHour,
                    endHour,
                    calculateZoomBasedHeight(zoomLevel, singleShort ? effectiveZoom : zoomIndex)
                );

                // Use row view if effective zoom is 1 or singleShort
                const useRowView = effectiveZoom === 1 || singleShort;
                const cardHeight = singleShort ? 60 : baseCardHeight;

                return (
                    <div
                        key={index}
                        className="tw-absolute tw-left-0 tw-right-0 tw-flex tw-flex-col tw-gap-4 tw-z-30"
                        style={{
                            top: `${startPosition.top}px`,
                            height: `${cardHeight}px`,
                        }}
                    >
                        {/* Presentation White Box */}
                        <div
                            className={`tw-shadow-md tw-flex tw-flex-col tw-bg-white tw-p-4 tw-relative tw-rounded-xl tw-w-80 md:tw-w-96 lg:tw-w-full  lg:tw-ml-0 tw-ml-3 ${zoomLevel[zoomIndex] === 100 ? 'tw-rounded-b-none' : ''}`}
                            style={{
                                height: (useRowView && zoomLevel[zoomIndex] <= 50) ? `${cardHeight}px` : `${baseCardHeight}px`,
                                zIndex: 30,
                            }}
                            onClick={() => {
                                if (isMobileOrTablet) {
                                    setSelectedClusterPresentation(pres);
                                    setIsDrawerOpen(true);
                                }
                            }}
                        >

                            {useRowView && zoomLevel[zoomIndex] <= 50 ? (
                                <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-gap-4 tw-h-full">
                                    <div className="tw-flex tw-items-center tw-gap-4">
                                        <img
                                            src={speakerImages[pres._id]}
                                            className="tw-w-10 tw-h-10 tw-object-cover tw-object-center tw-rounded-md"
                                            alt={speakerNames[pres._id] || "Speaker"}
                                        />
                                        <h2 className="tw-text-sm tw-font-bold tw-m-0" style={{ color: '#4B4F5A' }}>
                                            {pres.name}
                                        </h2>
                                    </div>
                                    {!isMobileOrTablet && (
                                        <div className="tw-flex tw-items-center tw-gap-4">
                                            {pres.association_id || pres.type_info || event?.quiz || event?.forum ? (
                                                <div
                                                    className="tw-cursor-pointer tw-flex tw-items-center"
                                                    onClick={() => toggleExpand(pres._id)}
                                                >
                                                    <img
                                                        src={expandedPresentations[pres._id] ? ChevronUp : ChevronDown}
                                                        alt="Chevron"
                                                        className="tw-w-4 tw-h-4 tw-transform tw-transition-transform 
                                                                tw-duration-150 hover:tw-scale-110"
                                                    />
                                                </div>
                                            ) : null}
                                            {userContext?.user?.id ? (
                                                <div className="tw-flex tw-items-center">
                                                    <img
                                                        src={scheduledPresentations[pres._id] ? removeFromSchedule : addtoschedule}
                                                        alt={scheduledPresentations[pres._id] ? "Remove from Schedule" : "Add to Schedule"}
                                                        onClick={() => updateScheduleStatus(pres._id, pres.event_id, scheduledPresentations[pres._id])}
                                                        className="tw-w-6 tw-h-6 tw-cursor-pointer tw-transform tw-transition-transform 
                                                                tw-duration-150 hover:tw-scale-110"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="tw-flex tw-items-center tw-gap-1">
                                                    <img width="18" height="11" src={views} alt="Views" />
                                                    <span>{plays}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : useRowView && zoomLevel[zoomIndex] === 1 ? (
                                <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-gap-4 tw-h-full">
                                    <div className="tw-flex tw-items-center tw-gap-4">
                                        <img
                                            src={speakerImages[pres._id]}
                                            className="tw-w-10 tw-h-10 tw-object-cover tw-object-center tw-rounded-md"
                                            alt={speakerNames[pres._id] || "Speaker"}
                                        />
                                        <h2 className="tw-text-sm tw-font-bold tw-m-0" style={{ color: '#4B4F5A' }}>
                                            {pres.name}
                                        </h2>
                                    </div>
                                    <div className="tw-flex tw-items-center tw-gap-4">
                                        {pres.association_id || pres.type_info || event?.quiz || event?.forum ? (
                                            <div
                                                className="tw-cursor-pointer tw-flex tw-items-center"
                                                onClick={() => toggleExpand(pres._id)}
                                            >
                                                <img
                                                    src={expandedPresentations[pres._id] ? ChevronUp : ChevronDown}
                                                    alt="Chevron"
                                                    className="tw-w-4 tw-h-4 tw-transform tw-transition-transform 
                                                                tw-duration-150 hover:tw-scale-110"
                                                />
                                            </div>
                                        ) : null}
                                        {userContext?.user?.id ? (
                                            <div className="tw-flex tw-items-center">
                                                <img
                                                    src={scheduledPresentations[pres._id] ? removeFromSchedule : addtoschedule}
                                                    alt={scheduledPresentations[pres._id] ? "Remove from Schedule" : "Add to Schedule"}
                                                    onClick={() => updateScheduleStatus(pres._id, pres.event_id, scheduledPresentations[pres._id])}
                                                    className="tw-w-6 tw-h-6 tw-cursor-pointer
                                                            tw-transform tw-transition-transform 
                                                            tw-duration-150 hover:tw-scale-110"
                                                />

                                            </div>

                                        ) : null}
                                    </div>
                                </div>
                            ) : (
                                <div className="tw-flex tw-justify-start tw-items-center tw-w-full tw-h-full tw-gap-4">
                                    <div className={`tw-flex tw-justify-center tw-flex-shrink-0 tw-h-full ${isMobileOrTablet ? " tw-items-start" : "tw-items-start "}`}>
                                        {isLoadingImages ? (
                                            <div className="tw-w-16 tw-h-24 tw-flex tw-items-center tw-justify-center">
                                                <div className="tw-w-8 tw-h-8 tw-border-4 tw-border-gray-200 tw-border-t-blue-500 tw-rounded-full tw-animate-spin"></div>
                                            </div>
                                        ) : (
                                            <img
                                                src={speakerImages[pres._id]}
                                                className={`tw-object-cover tw-object-center tw-rounded-md ${isMobileOrTablet ? 'tw-w-10' : 'tw-w-16 tw-h-full'}`}
                                                alt={speakerNames[pres._id] || "Speaker"}
                                            />
                                        )}
                                    </div>
                                    <div className="tw-w-full tw-min-w-0 tw-flex tw-flex-col tw-justify-between tw-h-full">
                                        {isMobileOrTablet ? (
                                            <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                                                <h2
                                                    className="tw-text-sm tw-font-bold tw-flex-1 tw-truncate"
                                                    style={{ color: '#4B4F5A' }}
                                                >
                                                    {pres.name}
                                                </h2>
                                                {userContext?.user?.id && scheduledPresentations[pres._id] && (
                                                    <div className="tw-flex tw-items-center tw-ml-2">
                                                        <img
                                                            src={removeFromSchedule}
                                                            alt="Remove from Schedule"
                                                            onClick={() => updateScheduleStatus(pres._id, pres.event_id, scheduledPresentations[pres._id])}
                                                            className="tw-w-6 tw-h-6 tw-cursor-pointer tw-transform tw-transition-transform 
                                                                tw-duration-150 hover:tw-scale-110"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                <div
                                                    className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-text-xs"
                                                    style={{ fontWeight: 700, color: '#4B4F5A' }}
                                                >
                                                    <span className="tw-font-semibold">{pres?.room_info?.name}</span>
                                                    <span>{formattedDate}</span>
                                                    <span>{timeFromMinutes(pres.time)} -{" "}
                                                        {timeFromMinutes(pres.end_time)}
                                                    </span>
                                                    {userContext?.user?.id ? (
                                                        <></>
                                                    ) : (
                                                        <div className="tw-flex tw-items-center tw-gap-1">
                                                            <img width="18" height="11" src={views} alt="Views" />
                                                            <span>{plays}</span>
                                                        </div>
                                                    )}
                                                    {!isMobileOrTablet && (
                                                        <div className="tw-flex tw-items-center">
                                                            <img
                                                                src={scheduledPresentations[pres._id] ? removeFromSchedule : addtoschedule}
                                                                alt={scheduledPresentations[pres._id] ? "Remove from Schedule" : "Add to Schedule"}
                                                                onClick={() => updateScheduleStatus(pres._id, pres.event_id, scheduledPresentations[pres._id])}
                                                                className="
                                                                tw-w-6 tw-h-6 tw-cursor-pointer  
                                                                tw-transform tw-transition-transform 
                                                                tw-duration-150 hover:tw-scale-110"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <h2
                                                    className={`tw-text-sm tw-font-bold ${effectiveZoom === 25 ? 'tw--mt-2' : 'tw-mt-1'}`}
                                                    style={{ color: '#4B4F5A', ...(effectiveZoom === 25 ? { marginTop: '-6px' } : {}) }}
                                                >
                                                    {pres.name}
                                                </h2>
                                                <div className="tw-flex-1 tw-flex tw-flex-col tw-justify-end">
                                                    <div className='tw-flex tw-flex-row tw-justify-between tw-items-center'>
                                                        <span className="tw-text-sm tw-truncate tw-text-black">
                                                            {pres.speaker_info?.title} {speakerNames[pres._id] || "Speaker"}
                                                        </span>
                                                        {pres.association_id || pres.type_info || event?.quiz || event?.forum ? (
                                                            zoomLevel[zoomIndex] !== 100 ? (
                                                                <div
                                                                    className="tw-cursor-pointer tw-mr-1 tw-flex tw-items-center"
                                                                    onClick={() => toggleExpand(pres._id)}
                                                                >
                                                                    <img
                                                                        src={expandedPresentations[pres._id] ? ChevronUp : ChevronDown}
                                                                        alt="Chevron"
                                                                        className="tw-w-4 tw-h-4 tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                                                                    />
                                                                </div>
                                                            ) : null
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Drawer Overlay */}
                        <div
                            className={`tw-absolute tw-left-0 tw-right-0 tw-transition-all tw-duration-500 tw-overflow-hidden
                        ${(expandedPresentations[pres._id] || zoomLevel[zoomIndex] === 100) ? 'tw-max-h-40 tw-opacity-100 tw-translate-y-2' : 'tw-max-h-0 tw-opacity-0 tw-translate-y-0'}`}
                            style={{
                                top: '100%',
                                zIndex: 25,
                            }}
                        >
                            {!isMobileOrTablet && (

                                <div className={`tw-text-sm tw-text-[#4B4F5A] tw-flex tw-flex-col ${zoomLevel[zoomIndex] === 100 ? 'tw-mx-0' : 'tw-mx-4'} ${zoomLevel[zoomIndex] > 50 && useRowView ? 'tw-mt-11' : ''}`}>
                                    {/* Top box */}
                                    {pres.type_info?.name || pres.association_info?.name ? 
                                        <div className="tw-shadow-sm" style={{ background: "#32363F" }}>
                                            <div className='tw-flex tw-flex-row tw-p-2'>
                                                {pres.type_info?.name ?
                                                    <div className='tw-ml-1 tw-flex tw-flex-row tw-items-center tw-gap-2 tw-cursor-pointer'>
                                                        {/* <span className='tw-rounded-md  tw-flex tw-items-center tw-justify-center' style={{ background: "#C4C4C4", border: "1px solid", borderColor: "#A5A8AF" }}>
                                                    </span> */}
                                                        <Calendar className='tw-text-white tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center' />

                                                        <span className='tw-text-white tw-text-xs'
                                                            style={{
                                                                fontWeight: 400
                                                            }}
                                                        >{pres.type_info?.name}</span>
                                                    </div>
                                                    : null}
                                                {pres.association_info?.name ?
                                                    <div className='tw-ml-6 tw-flex tw-flex-row tw-items-center tw-gap-2 tw-cursor-pointer'>
                                                        {/* <span className='tw-rounded-md tw-h-8 tw-w-12 tw-flex tw-items-center tw-justify-center' style={{ background: "#C4C4C4", border: "1px solid", borderColor: "#A5A8AF" }}>
                                                    </span> */}
                                                        <Users2 className='tw-text-white tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center' />
                                                        <span className='tw-text-white tw-text-xs'
                                                            style={{
                                                                fontWeight: 400
                                                            }}
                                                        >
                                                            {pres.association_info?.name}
                                                        </span>
                                                    </div>
                                                    : null}
                                            </div>
                                        </div>
                                    : null}

                                    {/* Bottom box */}
                                    {event?.forum && event?.quiz && (
                                        <div className="tw-shadow-sm" style={{ background: "#1080B2", borderBottomRightRadius: '0.625rem', borderBottomLeftRadius: '0.625rem' }}>
                                            <div className='tw-flex tw-flex-row tw-p-2'>
                                                <div className='tw-ml-1 tw-flex tw-flex-row tw-items-center tw-gap-1 tw-cursor-pointer tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110'
                                                    onClick={() => {
                                                        history.push(
                                                            `/${eventContext.slug}/${pres._id}/forum${document.location.search}`
                                                        );
                                                    }}>
                                                    <img src={quiz} alt="Quiz" />
                                                    <span className='tw-font-semibold tw-text-white tw-text-md'>FORUM</span>
                                                </div>
                                                <div className='tw-ml-11 tw-flex tw-flex-row tw-items-center tw-gap-1 tw-cursor-pointer tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110'
                                                    onClick={() => {
                                                        history.push(
                                                            `/${eventContext.slug}/${pres._id}/quiz-moderator${document.location.search}`
                                                        );
                                                    }}>
                                                    <img src={forum} alt="Forum" />
                                                    <span className='tw-font-semibold tw-text-white'>QUIZ</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                    </div>
                );
            })}
            <Cluster
                isDrawerOpen={isDrawerOpen}
                setIsDrawerOpen={setIsDrawerOpen}
                presentationsWithSameTime={
                    isMobileOrTablet && selectedClusterPresentation
                        ? [selectedClusterPresentation]
                        : (selectedClusterGroup ?? [])
                }
                isMobileOrTablet={isMobileOrTablet}
                speakerImages={speakerImages}
                speakerNames={speakerNames}
                scheduledPresentations={scheduledPresentations}
                updateScheduleStatus={updateScheduleStatus}
                totalPresentationsInPairs={
                    isMobileOrTablet && selectedClusterPresentation
                        ? 1
                        : (selectedClusterGroup?.length ?? 0)
                }
                day={day}
                rooms={rooms}
                event={event}
                timeGroupedPresentations={[]}
            />

            {
                clusterGroups.length > 0 ? (
                    <>
                        {clusterGroups.map((group, groupIndex) => {
                            const uniqueClusterList = Array.from(new Map(group.map(p => [p._id, p])).values());
                            const firstClusterPresentation = uniqueClusterList[0];
                            if (!firstClusterPresentation) return null;
                            const currentClusterKey = uniqueClusterList.map(p => p._id).sort().join("|");
                            const isActiveCluster = isDrawerOpen && selectedClusterGroupKey === currentClusterKey;

                            // Only show the cluster boxes if overlay is not open (on mobile/tablet)
                            if (isMobileOrTablet && isClusterDrawerOpen) return null;

                            const clusterPosition = calculatePosition(
                                firstClusterPresentation.time,
                                dynamicContainerHeight,
                                startHour,
                                endHour
                            );

                            return (
                                <div
                                    key={`cluster-${firstClusterPresentation._id}-${groupIndex}`}
                                    className={`tw-absolute tw-left-0 tw-right-0 tw-shadow-md tw-rounded-xl tw-px-2 tw-py-1 tw-cursor-pointer tw-flex tw-items-center tw-w-80 md:tw-w-96 lg:tw-ml-0 tw-ml-3 lg:tw-w-full tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-105`}
                                    onClick={
                                        isMobileOrTablet
                                            ? () => {
                                                setIsClusterDrawerOpen(true);
                                                setOpenedClusterPresentations(uniqueClusterList);
                                            }
                                            : () => {
                                                setSelectedClusterGroup(uniqueClusterList);
                                                setIsDrawerOpen(true);
                                            }
                                    }
                                    style={{
                                        background: !isMobileOrTablet && isActiveCluster ? "#32363F" : "#ffffff",
                                        top: `${clusterPosition.top}px`,
                                        zIndex: 20,
                                    }}
                                >
                                    <div className='tw-flex tw-flex-row tw-items-center tw-justify-between tw-w-full'>
                                        {isMobileOrTablet ? (
                                            <span className='tw-font-semibold tw-transition-colors tw-duration-300' style={{
                                                color: isActiveCluster ? "#ffffff" : "#1080B2",
                                            }}>
                                                {uniqueClusterList.length}
                                            </span>
                                        ) : (
                                            <span className='tw-font-semibold tw-transition-colors tw-duration-300' style={{
                                                color: isActiveCluster ? "#ffffff" : "#1080B2",
                                            }}>
                                                {uniqueClusterList.length} Presentations
                                            </span>
                                        )}
                                        {isMobileOrTablet ? (
                                            <div className="tw-flex tw-flex-row tw-items-center">
                                                {uniqueClusterList.map((pres, index) => (
                                                    <div
                                                        key={pres._id}
                                                        className={
                                                            `tw-relative tw-rounded-lg tw-overflow-hidden tw-shadow-md tw-transform tw-transition-all tw-duration-300 hover:tw-scale-105` +
                                                            (isMobileOrTablet ? ' tw-block' : ' tw-hidden')
                                                        }
                                                        style={{
                                                            marginLeft: index > 0 ? '-20px' : '0',
                                                            zIndex: 10000 - index,
                                                            border: '2px solid #ffffff',
                                                        }}
                                                    >
                                                        <img
                                                            src={speakerImages[pres._id]}
                                                            alt={`Speaker ${index + 1}`}
                                                            className="tw-w-8 tw-h-10"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <>
                                                {uniqueClusterList.map((pres, index) => (
                                                    <div
                                                        key={pres._id}
                                                        className={`tw-relative tw-rounded-lg tw-overflow-hidden tw-shadow-md tw-transform tw-transition-all tw-duration-300 hover:tw-scale-105  ${isMobileOrTablet ? 'tw-hidden' : 'tw-block'}`}
                                                        style={{
                                                            marginLeft: index > 0 ? '-155px' : '0',
                                                            zIndex: 10000,
                                                            border: '2px solid #ffffff',
                                                        }}
                                                    >
                                                        <img
                                                            src={speakerImages[pres._id]}
                                                            alt={`Speaker ${index + 1}`}
                                                            className="tw-w-8 tw-h-10"
                                                        />
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                    {isActiveCluster && <img src={leftArrow} alt="" className={`tw-w-4 tw-h-4 ${isMobileOrTablet ? 'tw-hidden' : 'tw-block'}`} />}
                                </div>
                            );
                        })}
                    </>
                ) : null
            }
            {isMobileOrTablet && isClusterDrawerOpen && (
                <div
                    className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-flex-col tw-items-center tw-justify-start tw-overflow-y-auto lg:tw-pb-0 tw-pb-16 tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-105"
                    style={{ background: '#32363F' }}
                >
                    <div className="tw-w-full tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between tw-border-b tw-bg-white tw-rounded-xl">
                        <span className="tw-font-semibold tw-text-base" style={{ color: "#1080B2" }}>
                            {openedClusterPresentations.length} presentations
                        </span>
                        <span
                            onClick={() => setIsClusterDrawerOpen(false)}
                            className="tw-cursor-pointer"
                        >
                            <img src={ChevronUp} alt="Close" className="tw-w-4 tw-h-4 tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-11" />
                        </span>
                    </div>

                    {openedClusterPresentations.map((pres, idx) => {
                        const date = new Date(pres.date);
                        const formattedDate = `${date.getDate()}/${date.toLocaleString('en-US', { month: 'short' })}`;

                        return (
                            <div className="tw-w-full tw-flex tw-flex-col">
                                <div key={pres._id} className={
                                    `tw-z-10 tw-flex tw-flex-col tw-mx-3 tw-bg-white` +
                                    (idx === openedClusterPresentations.length - 1 ? ' tw-rounded-b-xl' : '')
                                }>
                                    <div className="tw-p-2 sm:tw-p-3">
                                        <div className="tw-flex tw-justify-between tw-items-start tw-w-full tw-gap-2 sm:tw-gap-3">
                                            <div className="tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-h-full">
                                                <img
                                                    src={speakerImages[pres._id] || "/placeholder.svg"}
                                                    className="tw-w-12 tw-h-20 sm:tw-w-16 sm:tw-h-16 tw-object-cover tw-object-center tw-rounded-md"
                                                    alt={speakerNames[pres._id] || "Speaker"}
                                                />
                                            </div>
                                            <div className="tw-w-full tw-min-w-0">
                                                <div
                                                    className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-text-xs tw-gap-1"
                                                    style={{ fontWeight: 700, color: "#4B4F5A" }}
                                                >
                                                    <span className="tw-font-semibold tw-truncate tw-max-w-[120px] sm:tw-max-w-none">
                                                        {pres?.room_info?.name}
                                                    </span>
                                                    <span className="tw-whitespace-nowrap">{formattedDate}</span>
                                                    <span className="tw-whitespace-nowrap tw-text-[10px] sm:tw-text-xs">
                                                        {timeFromMinutes(pres.time)} - {timeFromMinutes(pres.end_time)}
                                                    </span>
                                                    <img
                                                        src={scheduledPresentations[pres._id] ? removeFromSchedule : addtoschedule}
                                                        alt={scheduledPresentations[pres._id] ? "Remove from Schedule" : "Add to Schedule"}
                                                        onClick={() =>
                                                            updateScheduleStatus(pres._id, pres.event_id, scheduledPresentations[pres._id])
                                                        }
                                                        className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6 tw-cursor-pointer tw-flex-shrink-0 tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                                                    />
                                                </div>
                                                <h2
                                                    className="tw-text-base sm:tw-text-lg tw-font-bold tw-mt-1 tw-line-clamp-2"
                                                    style={{ color: "#4B4F5A" }}
                                                >
                                                    {pres.name}
                                                </h2>
                                                <div className="tw-flex tw-flex-row tw-justify-between">
                                                    <span className="tw-text-xs sm:tw-text-sm tw-truncate tw-text-black">
                                                        {pres.speaker_info?.title} {speakerNames[pres._id] || "Speaker"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Top box */}
                                    {pres.association_id || pres.type_info ? 
                                        <div className="tw-shadow-sm" style={{ background: "#32363F" }}>
                                            <div className="tw-flex tw-flex-row tw-p-2 tw-gap-2 sm:tw-gap-4">
                                                {pres.type_info.name ?
                                                    <div className="tw-ml-1 tw-flex tw-flex-row tw-items-center tw-gap-2 tw-cursor-pointer">
                                                        {/* <span
                                                        className="tw-rounded-md tw-h-5 tw-w-5 sm:tw-h-6 sm:tw-w-6"
                                                        style={{ background: "#C4C4C4", border: "1px solid", borderColor: "#A5A8AF" }}
                                                        ></span> */}
                                                        <Calendar className='tw-text-white tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center' />

                                                        <span className="tw-text-white tw-text-sm sm:tw-text-xs tw-whitespace-nowrap">
                                                            {pres.type_info.name}
                                                        </span>
                                                    </div>
                                                    : null}
                                                {pres.association_info?.name ?
                                                    <div className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-cursor-pointer">
                                                        {/* <span
                                                        className="tw-rounded-md tw-h-6 tw-w-8 sm:tw-h-8 sm:tw-w-12"
                                                        style={{ background: "#C4C4C4", border: "1px solid", borderColor: "#A5A8AF" }}
                                                        ></span> */}
                                                        <Users2 className='tw-text-white tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center' />

                                                        <span className="tw-text-white tw-text-sm sm:tw-text-xs tw-whitespace-nowrap">
                                                            {pres.association_info?.name}
                                                        </span>
                                                    </div>
                                                    : null}
                                            </div>
                                        </div>
                                    : null}

                                    {/* Bottom box */}
                                    {event?.forum && event.quiz && (
                                        <div
                                            className="tw-shadow-sm"
                                            style={{
                                                background: "#4B4F5A",
                                                borderBottomRightRadius: "0.625rem",
                                                borderBottomLeftRadius: "0.625rem",
                                            }}
                                        >
                                            <div className="tw-flex tw-flex-row tw-p-3 tw-gap-4 sm:tw-gap-8">
                                                <div
                                                    className="tw-ml-1 tw-flex tw-flex-row tw-items-center tw-gap-1 tw-cursor-pointer tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                                                    onClick={() => {
                                                        history.push(`/${eventContext.slug}/${pres._id}/forum${document.location.search}`)
                                                    }}
                                                >
                                                    <img
                                                        src={quiz || "/placeholder.svg"}
                                                        alt="Quiz"
                                                        className="tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5"
                                                    />
                                                    <span className="tw-font-semibold tw-text-white tw-text-xs sm:tw-text-md">FORUM</span>
                                                </div>
                                                <div
                                                    className="tw-flex tw-flex-row tw-items-center tw-gap-1 tw-cursor-pointer tw-transform tw-transition-transform tw-duration-150 hover:tw-scale-110"
                                                    onClick={() => {
                                                        history.push(
                                                            `/${eventContext.slug}/${pres._id}/quiz-moderator${document.location.search}`,
                                                        )
                                                    }}
                                                >
                                                    <img
                                                        src={forum || "/placeholder.svg"}
                                                        alt="Forum"
                                                        className="tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5"
                                                    />
                                                    <span className="tw-font-semibold tw-text-white tw-text-xs sm:tw-text-sm">QUIZ</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div >
    );
}

export default PresentationCards;